import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { expose } from 'comlink';
import { collection, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, Query, query, startAfter, where } from 'firebase/firestore';
import { Warehouse } from '../../warehouse/warehouse.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { Issue } from '../../issue/issue.dto';
import { OPERATION } from '../../des.dto';
import { QueryConstraint } from '@firebase/firestore';
import { dtoConverter, HttpUtils, ISSUES_STORE, updateBatch, WAREHOUSES_STORE, WW_ISSUES_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseIssuesStepsWorker extends AbstractWarehouseDataSyncStepWorker<Issue> {
  constructor() {
    super(ISSUES_STORE, 'ISSUES');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseIssuesStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<Issue>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async push(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: Issue; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.debug('WarehouseIssuesStepsWorker::push', { division: ctx.division, category: 'sync' });
    return super.push(ctx, onProgress, itemsProcessedCallback);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: Issue[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    try {
      const q: Query = query(
        collection(getFirestore(), WW_ISSUES_COLLECTION),
        where('division._id', '==', ctx.division),
        orderBy('audit.lastModifiedDate'),
        ...queryConstraints,
      ).withConverter(dtoConverter({ syncId: ctx.syncId }));
      const queryResult = await getDocs(q);

      if (queryResult.docs.length && !this.shouldStopTask) {
        const issues = queryResult.docs.map((d) => d.data());

        try {
          logger.info('Downloaded issues from Firestore', { count: issues.length, category: 'sync' });
          await this.idbService.updateAll(ISSUES_STORE, issues).catch(console.error);
          return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
        } catch (err) {
          logger.error('Cannot update IDB', { error: err, category: 'sync' });
          throw err;
        }
      }
    } catch (err) {
      logger.error('Cannot download from Firestore', { error: err, category: 'sync' });
      throw err;
    }

    return [null, 0];
  }

  protected async updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    const warehouseData: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouse);

    if (warehouseData?.statistics?.issues?.total) {
      const newProgress = Math.ceil((downloaded / warehouseData.statistics?.issues?.total) * 100);
      logger.debug('Total items downloaded', { progress: newProgress, category: 'sync' });
      onProgress(newProgress, { processed: downloaded, expected: warehouseData.statistics.issues.total });
    }
  }

  protected async addRemoteData(data: Issue[]): Promise<void> {
    await HttpUtils.post(this.environment.appConfig.fns, 'ww-issueCreateBatch', { data: { issuesDetails: data } });
  }

  protected async deleteRemoteData(data: Issue[]): Promise<void> {
    const byWarehouse = data.reduce(
      (byWarehouse: Record<string, Issue[]>, issueMedia: Issue) => {
        if (!byWarehouse[issueMedia.division._id]) {
          byWarehouse[issueMedia.division._id] = [];
        }

        byWarehouse[issueMedia.division._id].push(issueMedia);
        return byWarehouse;
      },
      {} as Record<string, Issue[]>,
    );

    for (const warehouse of Object.keys(byWarehouse)) {
      try {
        const ids = byWarehouse[warehouse].map((t) => t._id);
        await HttpUtils.delete(this.environment.appConfig.fns, 'ww-issues/', {
          issueIds: JSON.stringify(ids),
          divisionId: warehouse,
        });
      } catch (err) {
        logger.error('Cannot delete on remote', { error: err, category: 'sync' });
      }
    }
  }

  protected async updateRemoteData(data: Issue[]): Promise<void> {
    const byWarehouse = data.reduce(
      (byWarehouse: Record<string, Issue[]>, issue: Issue) => {
        if (!byWarehouse[issue.division._id]) {
          byWarehouse[issue.division._id] = [];
        }

        byWarehouse[issue.division._id].push(issue);
        return byWarehouse;
      },
      {} as Record<string, Issue[]>,
    );

    for (const warehouse of Object.keys(byWarehouse)) {
      try {
        await updateBatch(WW_ISSUES_COLLECTION, byWarehouse[warehouse]);
      } catch (err) {
        logger.error('Cannot update on remote', { error: err, category: 'sync' });
      }
    }
  }
}

expose(WarehouseIssuesStepsWorker);
